import { ActionList, Table } from '@sqs/rosetta-compositions';
import { BackButton, Divider, Stack } from '@sqs/rosetta-elements';
import { Search } from '@sqs/rosetta-icons';
import { Box, Button, Flex, Text } from '@sqs/rosetta-primitives';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PAGINATION_LIMIT_MIN } from '../../const/pagination';
import { useGetSqspUserRoleMap } from '../../hooks/useGetSqspUserRoleMap';
import { usePlatformBreakpoint } from '../../hooks/usePlatformBreakpoint';
import { useSchedulingInstanceUserActions } from '../../hooks/useSchedulingInstanceUserActions';
import { useI18n } from '../../i18n';
import { ORG_MANAGER, OrgUser, SqspUser } from '../../models/SqspUser';
import { selectEnterprise } from '../../stores/currentUser';
import { RootState } from '../../stores/rootReducer';
import { fetchSchedulingInstances, selectById } from '../../stores/schedulingInstances';
import { fetchSchedulingInstanceUsers, selectAllSchedulingInstanceUsers } from '../../stores/schedulingInstanceUsers';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { enterpriseHasSsoProvider } from '../../utils/enterpriseUtils';
import { isInSqspOrg } from '../../utils/sqspUserUtils';
import { AcuityLoader } from '../common/AcuityLoader';
import { PermissionsMap } from '../common/PermissionsMap';
import { TableHeader } from '../common/Table/TableHeader';
import { TablePagination } from '../common/Table/TablePagination';
import { PageHeader } from '../PageHeader';
import { AddUserToInstance } from './AddUserToInstance/AddUserToInstance';
import { SchedulingInstanceUsersListTableData } from './SchedulingInstanceUsersListTableData';

const columnHelper = Table.Utils.createColumnHelper<OrgUser>();

export const SchedulingInstanceUsersList = () => {
  const { isMobile } = usePlatformBreakpoint();
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const { id } = params;
  const schedulingInstanceId = parseInt(id || '', 10);
  const { formatDateGeneric, T, t } = useI18n();
  const { getUserRole } = useGetSqspUserRoleMap();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const { currentUser, hasSsoProvider, enterpriseId, hasLoaded, schedulingInstanceUsers, instance } = useAppSelector(
    (state: RootState) => {
      const enterprise = selectEnterprise(state.currentUser);

      return {
        currentUser: state.currentUser.currentUser,
        enterpriseId: enterprise?.id,
        hasSsoProvider: enterpriseHasSsoProvider(enterprise),
        hasLoaded:
        state.schedulingInstanceUsers &&
        !state.currentUser.isLoading &&
        !state.schedulingInstanceUsers.isLoading &&
        !state.schedulingInstances.isLoading,
        schedulingInstanceUsers: selectAllSchedulingInstanceUsers(state.schedulingInstanceUsers),
        instance: selectById(state.schedulingInstances, schedulingInstanceId)
      };
    }
  );
  const { showRemoveUserFromSchedulingInstanceModal, showChangeUserPermissionModal, renderInstanceUserActionModals } =
  useSchedulingInstanceUserActions({
    refresh: () => {
      if (enterpriseId) {
        dispatch(fetchSchedulingInstanceUsers(enterpriseId, schedulingInstanceId));
      }
    }
  });

  useEffect(() => {
    if (!hasLoaded && schedulingInstanceId && enterpriseId) {
      dispatch(fetchSchedulingInstances(enterpriseId));
      dispatch(fetchSchedulingInstanceUsers(enterpriseId, schedulingInstanceId));
    }
  }, [hasLoaded, schedulingInstanceId, enterpriseId, dispatch]);

  useEffect(() => {
    // if we've loaded and:
    // - There is no instance found
    // - OR the user is not in a SQSP org
    // - OR the user is not an org manager
    // - OR the user does not have admin permissions
    // then redirect back to the dashboard
    // TODO: API needs to be modified to support passing down user permission for the current instance to finish this check
    //  https://squarespace.atlassian.net/browse/AE-1362
    // const userInInstance = schedulingInstanceUsers.filter((u) => u.id)[0];
    // const userHasPermission = userInInstance && userInInstance.role === ORG_MANAGER;
    if (hasLoaded && (!instance || !isInSqspOrg(currentUser))) {
      navigate('/dashboard');
    }
  }, [instance, hasLoaded, navigate, currentUser]);

  if (!currentUser && !hasLoaded && schedulingInstanceUsers.length === 0) {
    return null;
  }

  if (!hasLoaded || hasLoaded && !instance) {
    return (
      <Flex height="100%" justifyContent="center" mt={6} data-testid="scheduling-instance-user-list-loader">
        <AcuityLoader />
      </Flex>);

  }

  let dateAddedTooltip = t("The date a user accepted their invitation.", {}, { project: 'enterprise-dashboard' });
  const dateAddedLabel = t("Date active", {}, { project: 'enterprise-dashboard' });

  if (hasSsoProvider) {
    dateAddedTooltip = t("The date a user first signed in.", {}, { project: 'enterprise-dashboard' });
  }

  const mobileSearchButton = () => {
    return (
      <Button.Tertiary
        key="show-search"
        type="button"
        aria-label={t("Search", {}, { project: 'enterprise-dashboard' })}
        onClick={() => setShowSearch(!showSearch)}>

        <Search />
      </Button.Tertiary>);

  };

  const columns = [
  columnHelper.accessor('name', {
    header: ({ column }: any) =>
    <TableHeader label={t("Name", null, { project: 'enterprise-dashboard' })} column={column} />,

    cell: ({ getValue }) => {
      return (
        <Text.Body m={0} fontWeight={isMobile ? 500 : 'inherit'}>
            {getValue()}
          </Text.Body>);

    }
  }),
  columnHelper.accessor('role', {
    header: ({ column }: any) =>
    <TableHeader
      label={t("Role", null, { project: 'enterprise-dashboard' })}
      column={column}
      tooltip={t("This is the user's role within the organization.", {}, { project: 'enterprise-dashboard' })} />,


    cell: (cell) => {
      return (
        <Text.Body m={0} fontSize={3}>
            {getUserRole(cell.row.original.role)}
          </Text.Body>);

    }
  }),
  columnHelper.accessor('permissions', {
    header: ({ column }: any) => {
      return (
        <TableHeader
          label={t("Permissions", null, { project: 'enterprise-dashboard' })}
          column={column}
          tooltip={t("The permissions that this user has within the Scheduling Instance.",

          {}, {
            project: 'enterprise-dashboard' })
          } />);


    },
    cell: (cell) => {
      return <PermissionsMap permission={cell.getValue()?.permissions} />;
    }
  }),
  columnHelper.accessor('dateAdded', {
    header: ({ column }: any) => <TableHeader label={dateAddedLabel} column={column} tooltip={dateAddedTooltip} />,
    cell: (cell) => <Text.Body m={0}>{formatDateGeneric(cell.getValue())}</Text.Body>
  }),
  columnHelper.accessor('id', {
    enableSorting: false,
    header: t("Actions", null, { project: 'enterprise-dashboard' }),
    cell: (cell) => {
      const ariaLabel = t("Open User Actions Menu", null, { project: 'enterprise-dashboard' });
      const original: SqspUser = cell.row.original as SqspUser;
      const menuItems = ({ onRequestClose }: {onRequestClose: () => void;}) =>
      <Stack
        py={isMobile ? 0 : 1}
        direction="column"
        divider={isMobile && <Divider as="div" role="presentation" />}>

            <ActionList.Item
          as="div"
          onClick={() => {
            showChangeUserPermissionModal(schedulingInstanceId, cell.getValue(), original.permissions);
            onRequestClose();
          }}>

              <T project="enterprise-dashboard">{"Change permission"}</T>
            </ActionList.Item>
            <ActionList.Item
          as="div"
          onClick={() => {
            showRemoveUserFromSchedulingInstanceModal(schedulingInstanceId, cell.getValue());
            onRequestClose();
          }}
          color="red.400">

              <T project="enterprise-dashboard">{"Remove user from Instance"}</T>
            </ActionList.Item>
          </Stack>;


      // don't show actions for current user
      if (original.id === currentUser?.id || original.role === ORG_MANAGER) {
        return <></>;
      }

      if (isMobile) {
        return (
          <ActionList.Sheet
            aria-label={ariaLabel}
            renderHeader={() =>
            <Text.Body textAlign="center" fontWeight={500} pt={3}>
                  {cell.row.original.name}
                </Text.Body>
            }>

              {menuItems}
            </ActionList.Sheet>);

      }

      return (
        <ActionList.PopOver position="bottom" anchorPoint={{ x: 'center', y: 'top' }} aria-label={ariaLabel}>
            {menuItems}
          </ActionList.PopOver>);

    }
  })];

  const shouldShowSearch = (!isMobile || showSearch) && schedulingInstanceUsers.length > 0;

  return (
    <Box position="relative">
      <BackButton
        mt={isMobile ? 3 : 6}
        onClick={() => navigate('/')}
        label={t("Dashboard", {}, { project: 'enterprise-dashboard' })} />

      <PageHeader
        mt={0}
        actionButton={<AddUserToInstance schedulingInstanceId={schedulingInstanceId} showFullButton />}
        mobileActions={[
        mobileSearchButton(),
        <AddUserToInstance schedulingInstanceId={schedulingInstanceId} key="mobile-add" />]
        }
        title={t("Users for {instanceName}",

        { instanceName: instance?.instanceName || '' }, {
          project: 'enterprise-dashboard' })
        } />

      <Table
        columns={columns}
        data={schedulingInstanceUsers}
        enableSort
        enablePagination={schedulingInstanceUsers.length > 0}
        enableSearch={schedulingInstanceUsers.length > 0}
        initialState={{
          sorting: [
          {
            id: 'name',
            desc: false
          }],

          pagination: {
            pageIndex: 0,
            pageSize: PAGINATION_LIMIT_MIN
          }
        }}>

        <Table.Controls>
          <Table.Controls.Left>
            {isMobile &&
            <Table.Sort
              sortingOptions={[
              {
                id: 'name',
                ascLabel: t(" Name (A-Z)", {}, { project: 'enterprise-dashboard' }),
                descLabel: t(" Name (Z-A)", {}, { project: 'enterprise-dashboard' })
              },
              {
                id: t("status", {}, { project: 'enterprise-dashboard' }),
                ascLabel: t("User Status (A-Z)", {}, { project: 'enterprise-dashboard' }),
                descLabel: t("User Status (Z-A)", {}, { project: 'enterprise-dashboard' })
              }]
              } />

            }
            {shouldShowSearch &&
            <Table.Controls.Slot sx={{ marginBottom: isMobile ? 0 : 5 }}>
                <Table.Search isOpen={shouldShowSearch} />
                {!isMobile && <Table.RecordCount width="100%" />}
              </Table.Controls.Slot>
            }
          </Table.Controls.Left>
        </Table.Controls>
        <SchedulingInstanceUsersListTableData hasLoaded={hasLoaded} schedulingInstanceUsers={schedulingInstanceUsers} />
        {schedulingInstanceUsers.length > 0 ? <TablePagination /> : null}
      </Table>
      {renderInstanceUserActionModals()}
    </Box>);

};